import { useState } from 'react'
import {
  Box,
  Button,
  Card,
  Chip,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Typography,
} from '@mui/material'
import {
  Add,
  ArrowBack,
  Cancel,
  Delete,
  NavigateNext,
} from '@mui/icons-material'
import { useAppSelector } from 'src/store/hooks'
import { selectActiveLinkedService } from 'src/v3/manage/manage.slice'
import AddRequirementModal from './modals/add-requirement-modal'
import {
  ComplianceCredentialItemDraft,
  ComplianceLicenseItemDraft,
  ComplianceNREMTItemDraft,
  ComplianceRecertCycleItemDraft,
  formatCredentialType,
  formatLicenseType,
  formatRecertCycleItemType,
} from '../types'

type Props = {
  licenseItems: ComplianceLicenseItemDraft[]
  credentialItems: ComplianceCredentialItemDraft[]
  nremtItems: ComplianceNREMTItemDraft[]
  recertItems: ComplianceRecertCycleItemDraft[]
  onLicenseItemsChange: (items: ComplianceLicenseItemDraft[]) => void
  onCredentialItemsChange: (items: ComplianceCredentialItemDraft[]) => void
  onNREMTItemsChange: (items: ComplianceNREMTItemDraft[]) => void
  onRecertItemsChange: (items: ComplianceRecertCycleItemDraft[]) => void
  onCancel: () => void
  onBack: () => void
  onContinue: () => void
  canContinue: boolean
}

export default function ComplianceRequirementsStep({
  licenseItems,
  credentialItems,
  nremtItems,
  recertItems,
  onLicenseItemsChange,
  onCredentialItemsChange,
  onNREMTItemsChange,
  onRecertItemsChange,
  onCancel,
  onBack,
  onContinue,
  canContinue,
}: Props) {
  let linkedServiceID = useAppSelector(selectActiveLinkedService) ?? ''
  let [isModalOpen, setIsModalOpen] = useState(false)

  let handleRemoveLicense = (id: string) =>
    onLicenseItemsChange(licenseItems.filter((item) => item.id !== id))

  let handleRemoveCredential = (id: string) =>
    onCredentialItemsChange(credentialItems.filter((item) => item.id !== id))

  let handleRemoveNREMT = (id: string) =>
    onNREMTItemsChange(nremtItems.filter((item) => item.id !== id))

  let handleRemoveRecert = (id: string) =>
    onRecertItemsChange(recertItems.filter((item) => item.id !== id))

  let hasItems =
    licenseItems.length > 0 ||
    credentialItems.length > 0 ||
    nremtItems.length > 0 ||
    recertItems.length > 0

  return (
    <>
      <Card className="p-6">
        <Box className="flex flex-wrap items-center justify-between gap-2 mb-4">
          <Typography variant="h6" component="h2">
            Requirements
          </Typography>
          <Button
            variant="outlined"
            startIcon={<Add />}
            onClick={() => setIsModalOpen(true)}
          >
            Add Requirement
          </Button>
        </Box>

        {hasItems ? (
          <List>
            {licenseItems.map((item) => (
              <ListItem
                key={item.id}
                secondaryAction={
                  <IconButton
                    edge="end"
                    aria-label={`Remove license requirement ${item.type} - ${item.state}`}
                    onClick={() => handleRemoveLicense(item.id)}
                  >
                    <Delete />
                  </IconButton>
                }
              >
                <Chip label="License" size="small" className="mr-2" />
                <ListItemText
                  primary={`${formatLicenseType(item.type)} - ${item.state}`}
                />
              </ListItem>
            ))}
            {credentialItems.map((item) => (
              <ListItem
                key={item.id}
                secondaryAction={
                  <IconButton
                    edge="end"
                    aria-label={`Remove credential requirement ${item.type}`}
                    onClick={() => handleRemoveCredential(item.id)}
                  >
                    <Delete />
                  </IconButton>
                }
              >
                <Chip label="Credential" size="small" className="mr-2" />
                <ListItemText primary={formatCredentialType(item)} />
              </ListItem>
            ))}
            {nremtItems.map((item) => (
              <ListItem
                key={item.id}
                secondaryAction={
                  <IconButton
                    edge="end"
                    aria-label="Remove NREMT requirement"
                    onClick={() => handleRemoveNREMT(item.id)}
                  >
                    <Delete />
                  </IconButton>
                }
              >
                <Chip label="NREMT" size="small" className="mr-2" />
                <ListItemText primary="NREMT" />
              </ListItem>
            ))}
            {recertItems.map((item) => (
              <ListItem
                key={item.id}
                secondaryAction={
                  <IconButton
                    edge="end"
                    aria-label={`Remove recertification requirement`}
                    onClick={() => handleRemoveRecert(item.id)}
                  >
                    <Delete />
                  </IconButton>
                }
              >
                <Chip label="Recertification" size="small" className="mr-2" />
                <ListItemText primary={formatRecertCycleItemType(item)} />
              </ListItem>
            ))}
          </List>
        ) : (
          <Typography color="text.secondary">
            No requirements have been added yet.
          </Typography>
        )}
      </Card>

      <AddRequirementModal
        isOpen={isModalOpen}
        linkedServiceID={linkedServiceID}
        existingLicenseItems={licenseItems}
        existingCredentialItems={credentialItems}
        existingNREMTItems={nremtItems}
        existingRecertItems={recertItems}
        onClose={() => setIsModalOpen(false)}
        onAddLicenseItem={(item) =>
          onLicenseItemsChange([...licenseItems, item])
        }
        onAddCredentialItem={(item) =>
          onCredentialItemsChange([...credentialItems, item])
        }
        onAddNREMTItem={(item) => onNREMTItemsChange([...nremtItems, item])}
        onAddRecertItem={(item) => onRecertItemsChange([...recertItems, item])}
      />

      <Box className="flex justify-end gap-3 mt-6">
        <Button
          variant="outlined"
          onClick={onCancel}
          size="large"
          startIcon={<Cancel />}
        >
          Cancel
        </Button>
        <Button
          variant="outlined"
          onClick={onBack}
          size="large"
          startIcon={<ArrowBack />}
        >
          Back
        </Button>
        <Button
          variant="contained"
          onClick={onContinue}
          disabled={!canContinue}
          size="large"
          endIcon={<NavigateNext />}
        >
          Continue to Select Users
        </Button>
      </Box>
    </>
  )
}
